import { inject, provider } from "react-ioc";
import { observer } from "mobx-react";
import React, { Component } from "react";
import { Container, ContainerDetails } from "app/modules/common/layout/components/containers";
import { PageTitleDetails } from "app/modules/common/layout/components/page-title";
import { RateSettingsStore } from "app/modules/company/components/settings/rate-settings/RateSettingsStore";
import { Loading } from "app/modules/common/layout/components/loading";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Section, SectionName } from "app/modules/common/layout/components/details";
import { Region } from "app/modules/company/api/clients.api";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SectionTextField } from "app/modules/common/details/text-field";

@provider(RateSettingsStore)
@observer
export default class Index extends Component<any, any> {
    @inject rateSettingsStore: RateSettingsStore;

    render() {
        if (this.rateSettingsStore.isInitLoading) {
            return <Loading />;
        }

        return (
            <Container>
                <PageTitleDetails Title={this.rateSettingsStore.title} Size={12} />
                <ContainerDetails>
                    <SectionName
                        title="Rate Configuration"
                        description="Configure rates for different regions and vehicle types. Each origin region can have multiple rate groups for different destination regions."
                    >
                        <Stack spacing={2} sx={{ padding: "25px 15px 25px 15px" }}>
                            {this.rateSettingsStore.allRegions.map((region) => (
                                <Box key={region.id}>
                                    <Typography variant="subtitle1" gutterBottom align="right" sx={{ fontWeight: 'bold' }}>
                                        {region.name}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                                        {region.states.map((state) => (
                                            <Chip
                                                key={state}
                                                label={state}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </SectionName>

                    <Section>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Rate Settings Overview
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Configure rates per mile for different vehicle types (Sprinter, Small Straight, Large Straight)
                                    based on origin and destination regions. You can group multiple destination regions together
                                    with the same rate structure.
                                </Typography>
                            </Box>

                            {this.rateSettingsStore.ratesFormState.$.map((rateEntryForm, index) => (
                                <Accordion key={rateEntryForm.$.origin.value} defaultExpanded={index === 0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6">
                                            {this.rateSettingsStore.getRegionName(rateEntryForm.$.origin.value)} (Origin)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ width: '100%' }}>
                                            <Stack spacing={2}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="subtitle1">
                                                        Rate Groups
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<AddIcon />}
                                                        onClick={() => this.rateSettingsStore.addGroup(rateEntryForm.$.origin.value)}
                                                    >
                                                        Add Group
                                                    </Button>
                                                </Box>

                                                {rateEntryForm.$.groups.value.map((groupForm, groupIndex) => (
                                                    <Card key={`${rateEntryForm.$.origin.value}-group-${groupIndex}`} variant="outlined">
                                                        <CardContent>
                                                            <Stack spacing={2}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="subtitle2">
                                                                        Group {groupIndex + 1}
                                                                    </Typography>
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() => this.rateSettingsStore.removeGroup(rateEntryForm.$.origin.value, groupIndex)}
                                                                        disabled={rateEntryForm.$.groups.value.length <= 1}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Box>

                                                                <Divider />

                                                                <Box>
                                                                    <Typography variant="body2" gutterBottom>
                                                                        Destinations:
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                                        {groupForm.$.destinations.value.map((dest, destIndex) => (
                                                                            <Chip
                                                                                key={`${rateEntryForm.$.origin.value}-${groupIndex}-dest-${destIndex}`}
                                                                                label={this.rateSettingsStore.getRegionName(dest)}
                                                                                onDelete={() => this.rateSettingsStore.removeDestination(rateEntryForm.$.origin.value, groupIndex, dest)}
                                                                                color="primary"
                                                                                variant="outlined"
                                                                            />
                                                                        ))}
                                                                    </Box>

                                                                    <Select
                                                                        key={`${rateEntryForm.$.origin.value}-${groupIndex}-select-${this.rateSettingsStore.getAvailableDestinations(rateEntryForm.$.origin.value, groupIndex).length}`}
                                                                        value=""
                                                                        onChange={(e) => {
                                                                            const selectedDest = e.target.value as unknown as Region;
                                                                            if (selectedDest) {
                                                                                this.rateSettingsStore.addDestination(rateEntryForm.$.origin.value, groupIndex, selectedDest);
                                                                            }
                                                                        }}
                                                                        displayEmpty
                                                                        size="small"
                                                                        sx={{ minWidth: 200 }}
                                                                    >
                                                                        <MenuItem value="" disabled>
                                                                            Add Destination
                                                                        </MenuItem>
                                                                        {this.rateSettingsStore.getAvailableDestinations(rateEntryForm.$.origin.value, groupIndex).map(region => (
                                                                            <MenuItem key={region} value={region}>
                                                                                {this.rateSettingsStore.getRegionName(region)}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </Box>

                                                                <Divider />

                                                                <Box>
                                                                    <Typography variant="body2" gutterBottom>
                                                                        Rates per Mile:
                                                                    </Typography>
                                                                    <Grid container spacing={2}>
                                                                        <Grid item xs={12} sm={4}>
                                                                            <SectionTextField
                                                                                label="Sprinter"
                                                                                type="number"
                                                                                field={groupForm.$.rate.value.$.sprinter}
                                                                                width="100%"
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={4}>
                                                                            <SectionTextField
                                                                                label="Small Straight"
                                                                                type="number"
                                                                                field={groupForm.$.rate.value.$.smallStraight}
                                                                                width="100%"
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={4}>
                                                                            <SectionTextField
                                                                                label="Large Straight"
                                                                                type="number"
                                                                                field={groupForm.$.rate.value.$.largeStraight}
                                                                                width="100%"
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                ))}

                                                {this.rateSettingsStore.getUnusedDestinations(rateEntryForm.$.origin.value).length > 0 && (
                                                    <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
                                                        <Typography variant="body2" color="warning.contrastText">
                                                            Unassigned destinations: {this.rateSettingsStore.getUnusedDestinations(rateEntryForm.$.origin.value)
                                                                .map(r => this.rateSettingsStore.getRegionName(r))
                                                                .join(', ')}
                                                        </Typography>
                                                    </Paper>
                                                )}
                                            </Stack>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                                <LoadingButton
                                    loading={this.rateSettingsStore.isLoading}
                                    variant="contained"
                                    onClick={this.rateSettingsStore.saveRates}
                                    size="large"
                                >
                                    Save Rate Settings
                                </LoadingButton>
                            </Box>
                        </Stack>
                    </Section>
                </ContainerDetails>
            </Container>
        );
    }

    async componentDidMount() {
        await this.rateSettingsStore.init();
    }
}
